// The Morale instrument (GDD §15.1-1 + §15.1-4, M4): needle with the M* ghost
// and the arrival arrow drawn from the 5-year time constant, the drain/gain
// decomposition, the continuous capacity readout (v1.2.0 band — no cliff), and
// the warning ladder with countdowns in time units at the current cadence.

import type { Params } from '@lifespan/engine';
import { eta, type Clock } from '../game/clock.ts';
import { moraleModel, warningLadder, type Session } from '../game/driver.ts';

const W = 260;
const H = 150;
const CX = W / 2;
const CY = 130;
const R = 108;

/** angle for a 0–100 value across a 180° arc (left = 0, right = 100) */
const angle = (v: number) => Math.PI * (1 - v / 100);
const arcX = (v: number, r: number) => CX + r * Math.cos(angle(v));
const arcY = (v: number, r: number) => CY - r * Math.sin(angle(v));

export function MoraleMeter({
  session,
  P,
  clock,
}: {
  session: Session;
  P: Params;
  clock: Clock;
}) {
  const m = moraleModel(session, P);
  const ladder = warningLadder(session, P);
  // warn on the UNROUNDED capacity (in-band morale must never read as a clean
  // 100%), floored to 0.1% so a falling capacity is honest at the boundary
  const inBand = m.capacity < 1;
  const capacityStr = inBand ? (Math.floor(m.capacity * 1000) / 10).toFixed(1) : '100';
  // a dead life's morale is frozen: no drift, no arrow, no countdowns
  const drifting = m.alive && Math.abs(m.M - m.Mstar) > 0.5;
  const arrow = m.Mstar > m.M ? '↗' : '↘';

  // §15.1-1's arrival arrow, drawn from the 5-year time constant: an arc along
  // the needle's coming path from M toward M*, arrowhead at the equilibrium.
  // Its opacity is derived from the kM closed form's settle time — an imminent
  // arrival draws solid, a decade-long drift draws faint — so a slow and a
  // fast approach to the same M* look different on the dial itself.
  const AR = R - 32; // arrow arc radius (inside the needle tip)
  const settle = m.settleYears ?? 0.5; // within 5 pts of M* => effectively arrived
  const arrowOpacity = Math.max(0.25, Math.min(0.9, 0.9 - 0.055 * (settle - 1)));
  const tipX = arcX(m.Mstar, AR);
  const tipY = arcY(m.Mstar, AR);
  const arrowPath = `M ${arcX(m.M, AR)} ${arcY(m.M, AR)} A ${AR} ${AR} 0 0 ${m.Mstar > m.M ? 1 : 0} ${tipX} ${tipY}`;
  // arrowhead oriented along the arc's tangent at M* (direction of travel)
  const th = angle(m.Mstar);
  const dir = m.Mstar > m.M ? 1 : -1;
  const tx = Math.sin(th) * dir;
  const ty = Math.cos(th) * dir;
  const bx = tipX - 7 * tx;
  const by = tipY - 7 * ty;
  const arrowHead = `${tipX},${tipY} ${bx - 3.5 * ty},${by + 3.5 * tx} ${bx + 3.5 * ty},${by - 3.5 * tx}`;

  return (
    <section className="panel morale" aria-label="Morale">
      <div className="panel-title">Morale</div>
      <div className="morale-body">
        <svg
          className="morale-gauge"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={`Morale ${m.M.toFixed(0)} of 100, settling toward ${m.Mstar.toFixed(0)}`}
        >
          {/* track */}
          <path
            className="gauge-track"
            d={`M ${arcX(0, R)} ${arcY(0, R)} A ${R} ${R} 0 0 1 ${arcX(100, R)} ${arcY(100, R)}`}
            fill="none"
          />
          {/* burnout band shading (the v1.2.0 capacity ramp, 12–24) */}
          <path
            className="gauge-burn"
            d={`M ${arcX(P.burnoutLo, R)} ${arcY(P.burnoutLo, R)} A ${R} ${R} 0 0 1 ${arcX(P.burnoutHi, R)} ${arcY(P.burnoutHi, R)}`}
            fill="none"
          />
          {/* M* ghost tick */}
          <line
            className="gauge-ghost"
            x1={arcX(m.Mstar, R - 14)}
            y1={arcY(m.Mstar, R - 14)}
            x2={arcX(m.Mstar, R + 8)}
            y2={arcY(m.Mstar, R + 8)}
          />
          {/* arrival arrow: the needle's coming path, from the time constant */}
          {drifting && (
            <g className="gauge-arrow" opacity={arrowOpacity}>
              <path d={arrowPath} fill="none" />
              <polygon className="gauge-arrowhead" points={arrowHead} />
            </g>
          )}
          {/* needle */}
          <line
            className="gauge-needle"
            x1={CX}
            y1={CY}
            x2={arcX(m.M, R - 20)}
            y2={arcY(m.M, R - 20)}
          />
          <circle className="gauge-hub" cx={CX} cy={CY} r={5} />
          <text className="gauge-value" x={CX} y={CY - 32} textAnchor="middle">
            {m.M.toFixed(0)}
          </text>
          <text className="gauge-sub" x={CX} y={CY - 14} textAnchor="middle">
            {!m.alive
              ? 'final'
              : drifting
                ? `${arrow} M* ${m.Mstar.toFixed(0)}`
                : `at M* ${m.Mstar.toFixed(0)}`}
          </text>
        </svg>

        <div className="morale-lines">
          <div className="line">
            <span>Capacity</span>
            <span className={inBand ? 'warn' : ''}>
              {capacityStr}%{inBand ? ' — burning out' : ''}
            </span>
          </div>
          <div className="line">
            <span>Drains</span>
            <span>
              −{m.modeDrain}/yr work{m.studyDrain > 0 ? ` · −${m.studyDrain}/yr study` : ''}
            </span>
          </div>
          <div className="line">
            <span>Living buys</span>
            <span>+{m.gain.toFixed(1)}/yr</span>
          </div>
          {drifting && m.settleYears !== null && (
            <div className="line">
              <span>Settles</span>
              <span>{eta(m.settleYears, clock)}</span>
            </div>
          )}
        </div>
      </div>

      <ul className="ladder" aria-label="Morale warnings">
        {ladder.map((r) => (
          <li key={r.threshold} className={`rung ${r.status}`}>
            {r.status === 'below' && (
              <>
                <span className="rung-dot">●</span> {r.label} (below {r.threshold})
              </>
            )}
            {r.status === 'approaching' && (
              <>
                <span className="rung-dot">◔</span> {r.label} at {r.threshold} — in{' '}
                {eta(r.yearsToArrival!, clock)} at this plan
              </>
            )}
            {r.status === 'clear' && (
              <>
                <span className="rung-dot">○</span> {r.label} ({r.threshold}) — not on this path
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
